import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation  } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { toast, ToastContainer } from 'react-toastify';
import ReactModal from 'react-modal';
import imageCompression from 'browser-image-compression';

let orgChartScriptLoaded = false;
let orgChartLoadPromise = null;

const loadOrgChartScript = (src) => {
  if (orgChartScriptLoaded) {
    return Promise.resolve();
  }
  if (orgChartLoadPromise) {
    return orgChartLoadPromise;
  }

  orgChartLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      orgChartScriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return orgChartLoadPromise;
};
// --- End Global OrgChart script loader ---
function FamilyRequestAcceptedTree() {

    const navigate = useNavigate();

  const location = useLocation();
  const first_name = location.state?.first_name;
  const last_name = location.state?.last_name;
  const family_code = location.state?.family_code;
  


  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null); // Ref to store the OrgChart instance

  const queryClient = useQueryClient();

  const getToken = () => localStorage.getItem('token');

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState({
    id: '', name: '', alt_name: '', img: '', gender: '',
    married: '', single: '', single_mother: '', spouse: '', unknown: '',
  });

  // New state for chart-specific loading
  const [isChartLoading, setIsChartLoading] = useState(true); // Default to true to show spinner initially
  const [showSpinner, setShowSpinner] = useState(false); // State to control spinner visibility with delay

  const openModalWithData = (data) => {
    setSelectedMember({
      id: data.id || '',
      name: data.name || '',
      alt_name: data.alt_name || '',
      img: data.img || '',
      gender: data.gender || '',
      married: data.married || '',
      single: data.single || '',
      single_mother: data.single_mother || '',
      spouse: data.spouse || '',
      unknown: data.unknown || '',
    });
    setModalIsOpen(true);
  };

  // Query to fetch family tree data
  const { data: familyTreeData, isLoading: isLoadingFamilyTree, isError: isErrorFamilyTree } = useQuery({
    queryKey: ['familyTree', family_code],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-tree-accepted-api?family_code=${family_code}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        })
        .then((res) => res.data),
        enabled: !!family_code,
    staleTime: 0, // Data is immediately stale and will be refetched if re-rendered/accessed
    cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
  });

  // Handle spinner visibility with a delay to prevent flicker
  useEffect(() => {
    let timer;
    // Show spinner if either data is loading or chart is rendering, and only after 500ms
    if (isLoadingFamilyTree || isChartLoading) {
      timer = setTimeout(() => setShowSpinner(true), 500);
    } else {
      setShowSpinner(false); // Hide spinner immediately when not loading
    }
    return () => clearTimeout(timer); // Clear timer if state changes before 500ms
  }, [isLoadingFamilyTree, isChartLoading]);


  // Load OrgChart script and initialize/update chart when data is available
  useEffect(() => {
    if (!familyTreeData) {
      setIsChartLoading(true); // Keep chart loading state true until data is there
      return;
    }

    // Set chart loading to true while we prepare to render/update it
    setIsChartLoading(true);

    loadOrgChartScript(`${process.env.PUBLIC_URL}/orgchart/orgchart.js`)
      .then(() => {
        if (window.OrgChart) {
          initializeOrgChart(familyTreeData);
        }
      })
      .catch((error) => {
        console.error('Failed to load OrgChart script:', error);
        setIsChartLoading(false); // If script fails, stop loading
      });

    // Cleanup function for OrgChart instance
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [familyTreeData]); // Runs when familyTreeData updates


  const initializeOrgChart = (data) => {
    if (!window.OrgChart || !chartContainerRef.current) {
      setIsChartLoading(false); // Ensure loading is off if requirements not met
      return;
    }

    // This part is crucial: ALWAYS set loading to true when you start loading/updating the chart
    // and then rely on the 'ready' event to set it to false.
    setIsChartLoading(true);

    if (chartInstanceRef.current) {
      // If an instance already exists, load new data into it.
      // The 'ready' event listener is already attached or will be re-attached below.
      chartInstanceRef.current.load(data);
    } else {
      // Create a new instance if one doesn't exist
      const isMobile = window.innerWidth <= 768;
      const OrgChart = window.OrgChart;

      OrgChart.templates.family_template = Object.assign({}, OrgChart.templates.ana);
      OrgChart.templates.family_template.size = [86, 86];
      OrgChart.templates.family_template.plus = '';
      OrgChart.templates.family_template.minus = '';
      OrgChart.templates.family_template.rippleRadius = 40;
      OrgChart.templates.family_template.name = '<text style="font-size: 12px;" fill="#000000" x="43" y="100" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.title = '<text style="font-size: 12px;" fill="#aeaeae" x="43" y="115" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.img = '<clipPath id="{randId}"><circle cx="43" cy="43" r="38.5"></circle></clipPath></circle><image preserveAspectRatio="xMidYMid slice" clip-path="url(#{randId})" xlink:href="{val}" x="3" y="3" width="80" height="78"></image>';
      OrgChart.templates.family_template.node = '<circle stroke-width="3" fill="none" stroke="#FF1493" cx="43" cy="43" r="41.5"></circle>';

      OrgChart.templates.family_template_blue = Object.assign({}, OrgChart.templates.family_template);
      OrgChart.templates.family_template_blue.node = '<circle stroke-width="3" fill="none" stroke="#039BE5" cx="43" cy="43" r="41.5"></circle>';

      const chart = new OrgChart(chartContainerRef.current, {
        template: 'family_template',
        enableSearch: true,
        levelSeparation: isMobile ? 60 : 100,
        siblingSeparation: isMobile ? 60 : 100,
        scaleInitial: isMobile ? OrgChart.match.boundary : 1,
        scaleMin: 0.5,
        scaleMax: 3,
        nodeBinding: {
          name: 'name',
          title: 'title',
          img: 'img',
        },
        tags: {
          blue: { template: 'family_template_blue' },
          partner: { template: 'family_template' },
        },
        nodeMouseClick: OrgChart.action.none, // override default popup
      });

      chart.on('render-link', (sender, args) => {
        if (args.cnode.ppid !== undefined) {
          args.html += '<use xlink:href="#baby" x="' + args.p.xa + '" y="' + args.p.ya + '"/>';
        }
      });

      chart.on('redraw', function () {
        const chartElement = document.querySelector('#tree svg');
        if (chartElement) {
          chartElement.style.touchAction = 'pan-x pan-y';
        }
      });

      chart.on('click', (sender, args) => {
        if (args && args.node) {
          const nodeData = sender.get(args.node.id);
          setTimeout(() => {
            openModalWithData(nodeData);
          }, 150); // Slight delay fixes the flash
        }
      });

      chart.load(data);
      chartInstanceRef.current = chart;
    }

    // Ensure the 'ready' event listener is always attached/re-attached
    // This will set isChartLoading to false once the chart has finished rendering
    if (chartInstanceRef.current) {
        // Remove previous listener to prevent multiple calls if initializeOrgChart runs again
        // This is good practice if you re-attach listeners on every render
        chartInstanceRef.current.off('ready');
        chartInstanceRef.current.on('ready', () => {
            setIsChartLoading(false);
        });
    }
  };

  // Existing state and effects for modals, relationships, etc. (unchanged)
  const [addFamilyModalIsOpen, setAddFamilyModalIsOpen] = useState(false);
  const openAddFamilyModal = () => {
    setAddFamilyModalIsOpen(true);
  };
  const closeAddFamilyModal = () => {
    setAddFamilyModalIsOpen(false);
    resetForm(); // Reset form when closing
  };

  const [selectedRelationship, setSelectedRelationship] = useState('');
  const [isDeceased, setIsDeceased] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [isUnderage, setIsUnderage] = useState(false);
  const [selectedPrefix, setSelectedPrefix] = useState("");

  const getPrefixOptions = () => {
    const maleRelations = ['Father', 'Son', 'Brother'];
    const femaleRelations = ['Mother', 'Daughter', 'Sister', 'Spouse'];
    const neutral = ['Dr', 'Prof', 'Rev'];

    if (maleRelations.includes(selectedRelationship)) {
      return ['Mr', ...neutral];
    }
    if (femaleRelations.includes(selectedRelationship)) {
      return ['Mrs', ...neutral];
    }
    return ['Mr', 'Mrs', ...neutral];
  };

  // Spouse list
  const [spouseList, setSpouseList] = useState([]);
  useEffect(() => {
    if (addFamilyModalIsOpen && selectedMember) {
      fetchSpouses();
    }
  }, [addFamilyModalIsOpen, selectedMember]);

  const fetchSpouses = async () => {
    try {
        const response = await axios.post(
            `${API_URL}/family-tree-get-spouse-api`,
            {
              selected_id: selectedMember.id,
            },
            {
              withCredentials: true,
            }
          );

      setSpouseList(response.data);

      //console.log("Spouse API response:", response.data);

    } catch (error) {
      console.error('Error fetching spouses:', error);
    }
  };


  

  // Mother list
  const [motherList, setMotherList] = useState([]);
  useEffect(() => {
    if (addFamilyModalIsOpen && selectedMember) {
      fetchMothers();
    }
  }, [addFamilyModalIsOpen, selectedMember]);

  const fetchMothers = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/family-tree-get-mother-api`,
        {
          selected_id: selectedMember.id,
        },
        {
          withCredentials: true, // correct place
        }
      );
  

      setMotherList(response.data);
      
      //setMotherList(response.data.mothers || []);
    } catch (error) {
      console.error("Error fetching mothers:", error);
      setMotherList([]);
    }
  };

  

  const [hasFather, setHasFather] = useState(false);
  const [hasMother, setHasMother] = useState(false);
  useEffect(() => {
    if (selectedMember?.id) {
      axios.post(`${API_URL}/family-tree-check-father-mother-api`, {
        selected_id: selectedMember.id
      }, { withCredentials: true })
        .then(res => {
          setHasFather(res.data.hasFather);
          setHasMother(res.data.hasMother);
        })
        .catch(err => {
          console.error("Error fetching parent/spouse info:", err);
        });
    }
  }, [selectedMember]);

  const getRelationshipOptions = () => {
    const { gender, single, single_mother } = selectedMember || {};
    let baseOptions = [];

    if (gender === 'Male' && single_mother === "") {
      baseOptions = ['Father', 'Spouse'];
      if (hasFather) baseOptions.push('Mother');
      if (hasFather && hasMother) baseOptions.push('Step-Mother');
      if (spouseList.length > 0) {
        baseOptions.push('Son', 'Daughter');
      }
    } else if (gender === 'Male' && single_mother === 1) {
      
        baseOptions.push('Son', 'Daughter');
      

    } else if (gender === 'Female' && single === 1) {
      baseOptions = ['Father'];
      if (hasFather) baseOptions.push('Mother');
      if (hasFather && hasMother) baseOptions.push('Step-Mother');
    }

    if (hasFather) {
      baseOptions = baseOptions.filter(rel => rel !== 'Father');
    }
    if (hasMother) {
      baseOptions = baseOptions.filter(rel => rel !== 'Mother');
    }
    if (!(hasFather && hasMother)) {
      baseOptions = baseOptions.filter(rel => rel !== 'Step-Mother');
    }
    if (hasFather && hasMother) {
      baseOptions.push('Brother', 'Sister');
    }
    return baseOptions;
  };

  // Image preview
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Form data for adding member
  const [formData, setFormData] = useState({
    name: '', last_name: '', surname: '', gender: '', date_of_birth: '', date_of_death: '',
    phone_number: '', email: '', relationship: '', prefix: '',
    deceased: false, inactive: false, underage: false,
    selected_id: '', mother_id: ''
  });

  useEffect(() => {
    if (selectedMember) {
      setFormData((prev) => ({
        ...prev,
        selected_id: selectedMember.id,
      }));
    }
  }, [selectedMember]);

  // Helper to reset the form
  const resetForm = () => {
    setFormData({
      name: '', last_name: '', surname: '', gender: '', date_of_birth: '', date_of_death: '',
      phone_number: '', email: '', relationship: '', prefix: '',
      deceased: false, inactive: false, underage: false,
      selected_id: selectedMember?.id || '',
      mother_id: ''
    });
    setImageFile(null);
    setPreviewImage(null); // Reset preview image
    setSelectedRelationship('');
    setSelectedPrefix('');
    setIsDeceased(false);
    setIsInactive(false);
    setIsUnderage(false);
  };

  // useMutation for adding a family member
  const addMemberMutation = useMutation({
    mutationFn: async (submissionData) => {
      const response = await axios.post(
        `${API_URL}/family-tree-add-member-api`,
        submissionData,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['familyTree'] }); // Invalidate query on success
        closeAddFamilyModal();
        setModalIsOpen(false); // Close parent modal if open
        toast.success("Family member added successfully", { position: "top-right", autoClose: 3000 });
        resetForm();
      } else {
        toast.error(data.message || "Failed to add member", { position: "top-right", autoClose: 3000 });
      }
    },
    onError: (error) => {
      console.error('Error submitting form:', error);
      const backendMsg = error.response?.data?.message || error.message;
      toast.error(backendMsg || "An error occurred while adding member.", {
        position: "top-right",
        autoClose: 3000,
      });
      setIsChartLoading(false); // Important: if mutation fails, ensure spinner stops
    },
    onSettled: () => {
      // This runs regardless of success or error
      // The `familyTree` query invalidation will trigger the re-render and chart update,
      // and the `ready` event in `initializeOrgChart` will handle `setIsChartLoading(false)`.
    }
  });

  const handleSubmitMemberDetails = async () => {
    // Validation (unchanged from your code)
    if (!formData.relationship) {
      toast.error("Relationship is required", { position: "top-right", autoClose: 3000 });
      return;
    }

    const needsMotherId = ['Brother', 'Sister', 'Son', 'Daughter'].includes(formData.relationship);


    if (!formData.name) {
      toast.error("First name is required", { position: "top-right", autoClose: 3000 });
      return;
    }

    const isExemptFromContact = formData.deceased || formData.inactive || formData.underage;
    const hasPhoneOrEmail = formData.phone_number?.trim() || formData.email?.trim();

    if (!isExemptFromContact && !hasPhoneOrEmail) {
      toast.error("Please provide at least a phone number or an email", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Build FormData object
    const submissionData = new FormData();
    /*if (needsMotherId) {
      submissionData.append('mother_id', isSingleMother ? '0' : formData.mother_id);
    }*/

      if (needsMotherId) {
        if (!formData.mother_id) {
          toast.error("Mother is required for this relationship", { position: "top-right", autoClose: 3000 });
          return; // stop submission
        }
        submissionData.append('mother_id', formData.mother_id);
      }
      
    for (const key in formData) {
      if (
        key !== 'deceased' && key !== 'inactive' &&
        key !== 'underage' && key !== 'mother_id'
      ) {
        if (formData[key] !== undefined && formData[key] !== null) {
          submissionData.append(key, formData[key]);
        }
      }
    }
    submissionData.append('deceased', formData.deceased ? '1' : '0');
    submissionData.append('inactive', formData.inactive ? '1' : '0');
    submissionData.append('underage', formData.underage ? '1' : '0');

    // Compress image before sending
    if (imageFile) {
      try {
        const targetKB = 100;
        let quality = 0.9;
        let compressedFile = imageFile;

        while (true) {
          const options = {
            maxSizeMB: targetKB / 1024,
            initialQuality: quality,
            useWebWorker: true,
          };
          compressedFile = await imageCompression(imageFile, options);
          if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
          quality -= 0.1;
        }
        submissionData.append('image', compressedFile);
      } catch (err) {
        console.error('Image compression error:', err);
        toast.warn("Image compression failed. Uploading original image.", { position: "top-right" });
        submissionData.append('image', imageFile);
      }
    }
    if (selectedMember?.id) {
      submissionData.append('selected_member_id', selectedMember.id);
    }

    // Trigger the mutation
    addMemberMutation.mutate(submissionData);
  };



    const { data: treeExpired, isLoadingExpiry, isErrorExpiry } = useQuery({
      queryKey: ["userExpiryDetails"],
      queryFn: () =>
        axios
          .get(`${API_URL}/user-payment-details-api`, { withCredentials: true })
          .then((res) => res.data),
      refetchOnMount: "always",   //always fetch new data when component mounts
      refetchOnWindowFocus: true, //optional: also refresh when window regains focus
    });
    
    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    
    useEffect(() => {
      if (treeExpired && treeExpired.tree_expiry_date && treeExpired.expired) {
        setShowSubscribeModal(true);
      } else {
        setShowSubscribeModal(false); // hide if subscription is valid
      }
    }, [treeExpired]);
    
    
     
      const [loading, setLoading] = useState(false);
  
      const handleCheckPackages = async () => {
        setLoading(true);
      
        try {
          const response = await axios.post(
            `${API_URL}/checking-the-packages-subscription-api`,
            {},
            { withCredentials: true }
          );
      
          if (response.data.success) {
            const verifyCode = response.data.verify_code;
            navigate(`/tree-subscription-web?verifyCode=${verifyCode}`);
            
          } else {
            toast.error(response.data.message || "Something went wrong");
          }
        } catch (error) {
          toast.error("Error sending details");
          console.error(error);
        } finally {
          setLoading(false);
        }
      };
     


  if (isErrorFamilyTree) {
    return <p style={{ textAlign: 'center', color: 'red' }}>Error loading family tree data.</p>;
  }


  return (
    <div className="">
      <ToastContainer />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
              {first_name} {last_name} {family_code}




               {/* OrgChart Container - its visibility/opacity is controlled by the spinner */}
                          <div
                            id="tree"
                            ref={chartContainerRef}
                            style={{
                              width: '100%',
                              height: '650px',
                              opacity: showSpinner ? 0 : 1, // Chart is hidden when spinner is shown
                              transition: 'opacity 0.5s ease-in-out' // Smooth transition for showing/hiding chart
                            }}>
                          </div>
              
                          {/* Loading overlay with dimmed background */}
                          {showSpinner && (
                              
                            <div className="loadingOverlay" style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              flexDirection: 'column', // Changed to column to stack spinner and text
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent background
                              zIndex: 1000 // Ensure it's on top
                            }}>
                              <div className='loadingspinner'></div>
                              <p style={{ textAlign: 'center', color: '#000', marginTop: '10px' }}>Loading tree...</p> {/* Changed text color for better contrast */}
                            </div>
                          )}
              
              
              
                      <ReactModal  isOpen={modalIsOpen}  onRequestClose={() => setModalIsOpen(false)} 
                         className="Modal modal-md" overlayClassName="Overlay" ariaHideApp={false} >
              
                    <div className="modal-header" style={{backgroundColor: '#ffff'}}>
                      <h5 className="modal-title"></h5>
                      <button type="button" className="btn-close modalCloseButton" onClick={() => setModalIsOpen(false)}></button>
                    </div>
              
                      <div className="modal-body" style={{padding: '0'}}>
                        <div className="row">
                          <div className="col-12 col-md-12">
              
                          <div style={{height: '300px', overflow: 'hidden'}}>
                          {selectedMember.img && (
              
                            <img
                              src={selectedMember.img}
                              alt={selectedMember.name}
                              style={{ height: '300px', width: '100%', objectFit: 'cover', marginBottom: '15px' }}
                            />
                          )}
              
                        </div>
              
                        <center>
                          <h2 style={{ marginTop: '2%'}}>{selectedMember.name}</h2>
                          
                          
                          </center>
                          </div>
                        </div>
                      </div>
              
                      <div className="row" style={{paddingBottom: '5%'}}>
                        <div className="col-5 col-md-5" >
                        
              
                        {(selectedMember.spouse === "" && selectedMember.single === "") ? (
                                  <button
                                    type="submit"
                                    name="btn_extend_family"
                                    className="btn btn-outline-secondary font-size1"
                                    style={{ marginLeft: '5%' }}
                                    onClick={openAddFamilyModal}
                                  >
                                    <i className="fa fa-plus"></i> Add Family
                                  </button>
                                ) : selectedMember.single === "" ? (
                                    <></>
                                ) : (
                                   <></>
                                )}
                    
                  </div>
                  
                  <div className="col-3 col-md-3">
                  
                  
                  </div>
                    <div className="col-4 col-md-4">
              
              
                    </div>
                  
                </div>
              </ReactModal>
              
              
              
              <ReactModal 
                isOpen={addFamilyModalIsOpen}
                onRequestClose={closeAddFamilyModal}
                className="Modal modal-lg"
                overlayClassName="Overlay"
                ariaHideApp={false} >
                <div className="modal-header">
                  <h5 className="modal-title">Add yourself to <b>{selectedMember?.name}</b></h5>
                  <button type="button" className="btn-close modalCloseButton" onClick={closeAddFamilyModal}></button>
                </div>
              
                <div className="modal-body">
              
              <div className="row">
                <div className="col-md-12">
                  <center>
                    <div
                      className="position-relative d-inline-block"
                      style={{ cursor: 'pointer' }}
                      onClick={() => document.getElementById('uploadImageInput').click()}
                    >
                      <div className="">
                        <img
                          src={previewImage || 'img/avatar_male.png'} // fallback image
                          alt="Preview"
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '2px solid #ccc'
                          }}
                        />
                      </div>
                      <span
                        className="avatar-edit bg-transparent shadow-none"
                        style={{
                          position: 'absolute',
                          bottom: '5px',
                          right: '5px',
                          backgroundColor: '#fff',
                          borderRadius: '50%',
                          padding: '6px',
                          boxShadow: '0px 0px 5px rgba(0,0,0,0.2)'
                        }}
                      >
                        <i className="fas fa-edit" style={{ color: '#184A2C', fontSize: '16px' }}></i>
                      </span>
                      
                      <input
                        id="uploadImageInput"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                      
                          try {
                            // Compression options
                            const options = {
                              maxSizeMB: 1,            // Target max file size (in MB)
                              maxWidthOrHeight: 800,   // Resize (whichever side is larger)
                              useWebWorker: true,
                            };
                      
                            // Compress image
                            const compressedFile = await imageCompression(file, options);
                            setImageFile(compressedFile);
                      
                            // Create preview (from compressed file)
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setPreviewImage(reader.result);
                            };
                            reader.readAsDataURL(compressedFile);
                      
                          } catch (err) {
                            console.error("Image compression error:", err);
                            toast.error("Failed to compress image."); // Optional
                          }
                        }}
                      />
                      
                       
                    </div>
                  </center>
                </div>
              </div>
              
              
                 {/* Form fields for adding family member */}
                 <form onSubmit={(e) => { e.preventDefault(); handleSubmitMemberDetails(); }}>
                                  
                                  <div className='row'>
                                    <div className='col-6 col-md-6'>
                                    
                                      <>
                                      <label className="form-label">Who are you to this person?</label>
                                      <select
                                        name="relationship"
                                        className="form-control"
                                        value={formData.relationship}
                                        onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                                      >
                                        <option value="">Select to Add</option>
                                        {getRelationshipOptions().map((relation) => (
                                          <option key={relation} value={relation}>{relation}</option>
                                        ))}
                                      </select>
                                      </>
                              
                              
                                      
                                      
                                    </div>
                                    
                                    <div className='col-6 col-md-6'>
                                     
                              
                                    {(formData.relationship === 'Brother' || formData.relationship === 'Sister') && (
                                <>
                                  <label>Select Mother</label>
                                  <select
                                    className="form-control"
                                    value={formData.mother_id || ''}
                                    onChange={(e) => setFormData({ ...formData, mother_id: e.target.value })}
                                  >
                                    <option value="">Select Mother</option>
                                    {motherList.map((mother) => (
                                      <option key={mother.id} value={mother.id}>{mother.name}</option>
                                    ))}
                                  </select>
                                </>
                              )}
                              
                              {(formData.relationship === 'Son' || formData.relationship === 'Daughter'  && selectedMember.gender === 'Male') && (
                                <>
                                  <label>Select Mother</label>
                                  <select
                                    className="form-control"
                                    value={formData.mother_id || ''}
                                    onChange={(e) => setFormData({ ...formData, mother_id: e.target.value })}
                                  >
                                    <option value="">Select Mother</option>
                                    {spouseList.map((spouse) => (
                                      <option key={spouse.id} value={spouse.id}>{spouse.name}</option>
                                    ))}
                                  </select>
                                </>
                              )}
                              
                               
                                    </div>
                                  </div>
                              
                                  <div className='row'>
                                    <div className='col-6 col-md-6'>
                                      <label>Prefix</label>
                                      <select
                                        className="form-control"
                                        value={formData.prefix}
                                        onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                                      >
                                        <option value="">Select Prefix</option>
                                        {getPrefixOptions().map((prefix) => (
                                          <option key={prefix} value={prefix}>{prefix}</option>
                                        ))}
                                      </select>
                                    </div>
                                    
                                    <div className='col-6 col-md-6'>
                                      <label>First Name</label>
                                      <input
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                      />
                                    </div>
                                  </div>
                              
                                  <div className='row'>
                                    <div className='col-6 col-md-6'>
                                      <label>Last Name</label>
                                      <input
                                        type="text"
                                        name="last_name"
                                        className="form-control"
                                        value={formData.last_name}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                      />
                                    </div>
                              
                                    <div className='col-6 col-md-6'>
                                      <label>Surname</label>
                                      <input
                                        type="text"
                                        name="surname"
                                        className="form-control"
                                        value={formData.surname}
                                        onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                                      />
                                    </div>
                                  </div>
                              
                                  <div className='row'>
                                    <div className='col-6 col-md-6'>
                                      <label>Status</label><br />
                              
                                      <input
                                        type="checkbox"
                                        name="deceased"
                                        className="form-check-input me-2"
                                        checked={formData.deceased}
                                        onChange={() => {
                                          setFormData((prev) => ({
                                            ...prev,
                                            deceased: !prev.deceased,
                                            inactive: false,
                                            underage: false
                                          }));
                                        }}
                                      /> Deceased<br />
                              
                                      <input
                                        type="checkbox"
                                        name="inactive"
                                        className="form-check-input me-2"
                                        checked={formData.inactive}
                                        onChange={() => {
                                          setFormData((prev) => ({
                                            ...prev,
                                            inactive: !prev.inactive,
                                            deceased: false,
                                            underage: false
                                          }));
                                        }}
                                      /> Inactive<br />
                              
                                      {!['Father', 'Mother', 'Spouse', 'Step-Mother'].includes(formData.relationship) && (
                                        <div>
                                          <input
                                            type="checkbox"
                                            name="underage"
                                            className="form-check-input me-2"
                                            checked={formData.underage}
                                            onChange={() => {
                                              setFormData((prev) => ({
                                                ...prev,
                                                underage: !prev.underage,
                                                deceased: false,
                                                inactive: false
                                              }));
                                            }}
                                          /> Under-age<br />
                                        </div>
                                      )}
                                    </div>
                              
                                    <div className='col-6 col-md-6'>
                                      <label>Date of Birth</label>
                                      <input
                                        type="date"
                                        name="date_of_birth"
                                        className="form-control"
                                        value={formData.date_of_birth}
                                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                      />
                              
                                      {formData.deceased && (
                                        <>
                                          <label>Date of Death</label>
                                          <input
                                            type="date"
                                            name="date_of_death"
                                            className="form-control"
                                            value={formData.date_of_death}
                                            onChange={(e) => setFormData({ ...formData, date_of_death: e.target.value })}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>
                              
                                  <div className='row'>
                                    {!(formData.deceased || formData.inactive || formData.underage) && (
                                      <>
                                        <div className='col-6 col-md-6'>
                                          <label>Phone Number</label>
                                          <input
                                            type="text"
                                            name="phone_number"
                                            className="form-control"
                                            value={formData.phone_number}
                                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                          />
                                        </div>
                              
                                        <div className='col-6 col-md-6'>
                                          <label>Email</label>
                                          <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                          />
                                        </div>
                                      </>
                                    )}
                                  </div>
                  
                  
                                    <div className="submit-section">
                                      <button
                                        type="submit"
                                        className="btn btn-primary submit-btn"
                                        disabled={addMemberMutation.isPending} // Disable button during submission
                                      >
                                        {addMemberMutation.isPending ? 'Saving...' : 'Save Your Details'}
                                      </button>
                                    </div>
                                  </form>
              
                </div>
              </ReactModal>




              </div>
            </div>
          </div>
          <div className="row end-page-space"></div>
        </div>
      </div>
    </div>
  );
}

export default FamilyRequestAcceptedTree;